import React from 'react';
import { Check, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PasswordStrengthIndicatorProps {
  password: string;
}

export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({ password }) => {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
  };

  const strength = Object.values(checks).filter(Boolean).length;

  const getStrengthLabel = () => {
    if (strength === 0) return '';
    if (strength <= 2) return 'Weak';
    if (strength <= 3) return 'Fair';
    if (strength <= 4) return 'Good';
    return 'Strong';
  };

  const getStrengthColor = () => {
    if (strength <= 1) return 'bg-red-500';
    if (strength <= 2) return 'bg-orange-500';
    if (strength <= 3) return 'bg-yellow-500';
    if (strength === 4) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getTextColor = () => {
    if (strength <= 1) return 'text-red-600';
    if (strength <= 2) return 'text-orange-600';
    if (strength <= 3) return 'text-yellow-600';
    if (strength === 4) return 'text-blue-600';
    return 'text-green-600';
  };

  if (!password) return null;

  return (
    <div className="space-y-3 mt-2">
      {/* Strength bar */}
      <div className="space-y-1">
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">Password strength</span>
          <span className={cn('text-xs font-medium', getTextColor())}>
            {getStrengthLabel()}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={cn('h-full rounded-full transition-all duration-300', getStrengthColor())}
            style={{ width: `${(strength / 5) * 100}%` }}
          />
        </div>
      </div>

      {/* Requirements checklist */}
      <ul className="space-y-1">
        <li className={cn(
          'flex items-center text-xs',
          checks.length ? 'text-green-600' : 'text-gray-500'
        )}>
          {checks.length ? (
            <Check className="w-3.5 h-3.5 mr-1.5" />
          ) : (
            <Circle className="w-3.5 h-3.5 mr-1.5" />
          )}
          At least 8 characters
        </li>
        <li className={cn(
          'flex items-center text-xs',
          checks.uppercase ? 'text-green-600' : 'text-gray-500'
        )}>
          {checks.uppercase ? (
            <Check className="w-3.5 h-3.5 mr-1.5" />
          ) : (
            <Circle className="w-3.5 h-3.5 mr-1.5" />
          )}
          One uppercase letter
        </li>
        <li className={cn(
          'flex items-center text-xs',
          checks.lowercase ? 'text-green-600' : 'text-gray-500'
        )}>
          {checks.lowercase ? (
            <Check className="w-3.5 h-3.5 mr-1.5" />
          ) : (
            <Circle className="w-3.5 h-3.5 mr-1.5" />
          )}
          One lowercase letter
        </li>
        <li className={cn(
          'flex items-center text-xs',
          checks.number ? 'text-green-600' : 'text-gray-500'
        )}>
          {checks.number ? (
            <Check className="w-3.5 h-3.5 mr-1.5" />
          ) : (
            <Circle className="w-3.5 h-3.5 mr-1.5" />
          )}
          One number
        </li>
        <li className={cn(
          'flex items-center text-xs',
          checks.special ? 'text-green-600' : 'text-gray-500'
        )}>
          {checks.special ? (
            <Check className="w-3.5 h-3.5 mr-1.5" />
          ) : (
            <Circle className="w-3.5 h-3.5 mr-1.5" />
          )}
          One special character (!@#$%^&*)
        </li>
      </ul>
    </div>
  );
};
